import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Music2, Play, Pause, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const LANG_TABS = [
  { code: "all", label: "All" },
  { code: "hi", label: "हिन्दी Hindi" },
  { code: "en", label: "English" },
];

const fmtTime = (s) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function Songs() {
  const [songs, setSongs] = useState([]);
  const [lang, setLang] = useState("all");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const q = lang === "all" ? "" : `?language=${lang}`;
    api.get(`/songs${q}`).then(r => setSongs(r.data || []));
  }, [lang]);

  const togglePlay = (song) => {
    if (playingId === song.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    setPlayingId(song.id);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.src = song.audio_url;
        audioRef.current.play().catch(() => setPlayingId(null));
      }
    }, 50);
  };

  return (
    <div className="px-4 py-6 pb-24" data-testid="songs-page">
      <Link to="/more" className="flex items-center gap-1 text-xs text-white/60 mb-3" data-testid="songs-back"><ArrowLeft className="w-3.5 h-3.5"/>Back</Link>
      <div className="flex items-center gap-2 mb-1">
        <Music2 className="w-7 h-7 text-[#A855F7]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Songs</h1>
      </div>
      <p className="text-white/60 text-sm mb-4">Trending songs in Hindi &amp; English to use for your reels</p>

      {/* Language tabs */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="songs-lang-tabs">
        {LANG_TABS.map(t => (
          <button key={t.code} onClick={() => setLang(t.code)} data-testid={`song-lang-${t.code}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition ${lang === t.code
              ? "bg-[#A855F7] text-white border-[#A855F7]"
              : "bg-white/5 text-white/70 border-white/10 hover:border-white/30"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden"/>

      <div className="space-y-2" data-testid="songs-list">
        {songs.map(s => (
          <div key={s.id} data-testid={`song-${s.id}`}
            className={`flex items-center gap-3 p-2.5 rounded-2xl border ${playingId === s.id ? "border-[#A855F7] bg-[#A855F7]/10" : "border-white/10 bg-white/5"} transition`}>
            <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0"
              style={{ backgroundImage: s.cover_url ? `url(${s.cover_url})` : undefined, backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#1a1f2e" }}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="font-outfit font-bold text-sm text-white truncate">{s.title}</div>
                <span className="px-1.5 py-0 rounded-full text-[9px] font-extrabold tracking-wider"
                  style={{ background: s.language === "hi" ? "#FFD70022" : "#00E5FF22", color: s.language === "hi" ? "#FFD700" : "#00E5FF", border: `1px solid ${s.language === "hi" ? "#FFD70055" : "#00E5FF55"}` }}>
                  {s.language === "hi" ? "HI" : "EN"}
                </span>
              </div>
              <div className="text-xs text-white/60 truncate">{s.artist}</div>
              <div className="text-[10px] text-white/40 mt-0.5">{fmtTime(s.duration_seconds)}</div>
            </div>
            <button onClick={() => togglePlay(s)} data-testid={`song-play-${s.id}`}
              className="w-11 h-11 rounded-full bg-[#A855F7] text-white flex items-center justify-center active:scale-95 shadow-lg shadow-[#A855F7]/30">
              {playingId === s.id ? <Pause className="w-5 h-5"/> : <Play className="w-5 h-5 ml-0.5 fill-white"/>}
            </button>
          </div>
        ))}
        {songs.length === 0 && <p className="text-center text-white/40 text-sm py-8">No songs yet in this language.</p>}
      </div>
    </div>
  );
}
